import * as auth from 'src/authConfig';
import UserABMModel from 'src/models/UserABMModel';
import { IUserABM } from 'src/models/interfaces/IUserABM';
import ErrorService, { EDPError } from './Error.service';

// MSAL imports
import { PublicClientApplication, EventType, AuthenticationResult } from "@azure/msal-browser";

/**
 * @constant msalInstance
 * @description Inicializo el cliente para la authenticación
 */
export const msalInstance = new PublicClientApplication(auth.msalConfig);

msalInstance.initialize().then(() => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });
});

/**
 * @function getAccessToken
 * @description Obtiene el Access Token del usuario logueado
 * @returns {Promise<string>}
 */
export async function getAccessToken(): Promise<string> {
    const account = msalInstance.getActiveAccount();
    if (!account) {
        throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }

    const response = await msalInstance.acquireTokenSilent({
        ...auth.loginRequest,
        account: account
    });

    return response.accessToken;
}

/**
 * @function getIdToken
 * @description Obtiene el ID Token del usuario logueado (más corto que accessToken)
 * @returns {Promise<string>}
 */
export async function getIdToken(): Promise<string> {
    const account = msalInstance.getActiveAccount();
    if (!account) {
        throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }

    const response = await msalInstance.acquireTokenSilent({
        ...auth.loginRequest,
        account: account
    });

    return response.idToken;
}

/**
 * @function callMsGraph
 * @description Obtiene la informaci�n del usuario logueado.
 * @returns {Promise<JSON | null>}
 */
export async function callMsGraph(): Promise<JSON | null> {
  const accessToken = await getAccessToken();
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append("Authorization", bearer);
  headers.append("credentials", "include");
  headers.append("contentType", "application/json; charset=utf-8");

  const options = {
    method: "GET",
    headers: headers
  };

  return fetch(auth.graphConfig.graphMeEndpoint, options)
    .then((response: Response) => {
      return response.json();
    }).catch((err: any) => {
      ErrorService(
        new EDPError(
          err.message ?? "authService - callMsGraph",
          "error",
          undefined,
          err.stack
        )
      );
      return null;
    });
}

/** 
 * @function callProfilePhoto
 * @description Obtiene la foto del usuario logueado.
 * @returns {Promise<ArrayBuffer | null>}
 */
export async function callProfilePhoto(): Promise<ArrayBuffer | null> {
  const accessToken = await getAccessToken();
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append("Authorization", bearer);

  const options = {
    method: "GET",
    headers: headers
  };

  return fetch(auth.graphConfig.graphPhotoEndpoint, options)
    .then((response: Response) => {
      return response.status === 200 ? response.arrayBuffer() : null;
    }).catch((err: any) => {
      ErrorService(
        new EDPError(
          err.message ?? "authService - callProfilePhoto",
          "error",
          undefined,
          err.stack
        )
      );
      return null;
    });
}

/**
 * Obtiene datos de un usario a partir de un email
 * @param {string} email Email a partir del cual obtengo información
 * @returns {Promise<JSON | null>}
 */
export async function getUserDataByEmail(email: string): Promise<IUserABM | null> {
  const accessToken = await getAccessToken();
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append("Authorization", bearer);

  const options = {
    method: "GET",
    headers: headers
  };

  return fetch(`${auth.graphConfig.graphUsersEndpoint}${email}?$select=givenName,surname,mail`, options)
    .then((response: Response) => {
      return response.status === 200 ? response.json() : null;
    }).then((response: any) => {
      const data: UserABMModel = new UserABMModel();
      if (response) {
        data.id = response.id ?? "";
        data.firstName = response.givenName ?? "";
        data.lastName = response.surname ?? "";
        data.email = response.mail ?? "";
        data.language = response.officeLocation ?? "en";
        data.active = true;
      }
      return data;
    }).catch((err: any) => {
      ErrorService(
        new EDPError(
          err.message ?? "authService - graphUsersEndpoint",
          "error",
          undefined,
          err.stack
        )
      );
      return null;
    });
}
